import { injectable, inject } from 'inversify';
import pako from 'pako';
import Debug from 'debug'
import { ok, err, Result, SystemDslModule } from '@xlearn/common'
import * as base64 from "byte-base64";
import { createGenericUpserter, waitToLoad } from '@/app/shared/utils'
import * as Common from '../../../common';
import { ErrorHandlingScorm2004Client, ErrorHandlingScorm2004ClientSymbol } from '@/app/tracking/scorm/2004/client';
import { StateSerializationFactory, StateSerializationFactorySymbol,
  stateSerializerName,
  StateSerializationPlugin} from '@/app/state-serialization/api';
import { ScormSuspendDataState, ScormSuspendDataStateItem } from './scorm-suspend-data-state'
import { reportNonFatalError } from '@/app/error-handling';

interface SuspendDataState {
  [ key: string]: unknown
}

// function defaultSuspendDataState(): SuspendDataState {
//   return {}
// }

const upserter = createGenericUpserter<'name', ScormSuspendDataStateItem> (
  'name', (name) => ({ name: name, content: null }))

const debug = Debug('scorm2004-suspend-data-store')

// TODO: refactor withPrefetch

@injectable()
export class Scorm2004SuspendDataStore implements Common.SuspendDataStore<SuspendDataState> {

  // private suspendData: SuspendDataState | undefined;
  private data: ScormSuspendDataState = []
  //private dataFetched: boolean = false
  private fetchState: 'none' | 'fetching' | 'fetched'

  private serializer: StateSerializationPlugin<ScormSuspendDataState>
  
  constructor(
    @inject(ErrorHandlingScorm2004ClientSymbol)
    private readonly client: ErrorHandlingScorm2004Client,
    @inject(StateSerializationFactorySymbol)
    serializerFactory: StateSerializationFactory) {

    const name = stateSerializerName('scorm2004-tracking', 'suspend-data')
    const serializerResult = serializerFactory.resolveByName<ScormSuspendDataState>(name, 'protobuf')
    if (!serializerResult.success) {
      throw new Error(
        `Coding error: state serializer not found with name '${JSON.stringify(name)}' and format 'protobuf'`)
    }

    this.serializer = serializerResult.value
  }

  public async get2(key: keyof SuspendDataState):
    Promise<Result<SuspendDataState[keyof SystemDslModule] | null, Common.SuspendDataStoreError>> {

    debug('get', key)
    
    const result = await this.prefetchRemote2();
    debug('get load result', result)
    if (!result.success) {
      // TODO: error handling
      reportNonFatalError('State load error', result.error)
      return result
    }

    const found = result.value.find(x => x.name === key)
    if (!found) {
      return ok(null)
    }
    
    return ok(found.content as SuspendDataState[keyof SuspendDataState]);
  }

  // public async get(aspect: keyof SuspendDataState) {
  //   const result = this.prefetchRemote();
  //   if (!result.success) {
  //     return result;
  //   }
  //   return ok(result.value[aspect]);
  // }

  // public async update(aspect: keyof SuspendDataState, data: SuspendDataState[keyof SuspendDataState]) {
  //   const result = this.prefetchRemote();
  //   if (!result.success) {
  //     return result;
  //   }

  //   result.value[aspect] = data;
  //   this.saveRemote(result.value);

  //   return ok(true as true);
  // }

  public async update2(key: string,
                       data: SuspendDataState[keyof SuspendDataState]) {

    debug('update', key, data)
    
    const result = await this.prefetchRemote2();
    if (!result.success) {
      return result
    }

    this.data = upserter(this.data, key as string, (item) => {
      return {
        name: item.name,
        content: data
      }
    })

    const saveResult = this.saveRemote2(this.data);
    debug('update result', saveResult)

    return saveResult
  }

  // private prefetchRemote() {
  //   if (!this.suspendData) {
  //     const remote = this.loadRemote();
  //     if (!remote.success) {
  //       return remote;
  //     }

  //     this.suspendData = remote.value;
  //   }

  //   return ok(this.suspendData as SuspendDataState);
  // }

  private async prefetchRemote2() {

    if (this.fetchState === 'fetched') {
      return ok(this.data)
    }

    if (this.fetchState === 'fetching') {
      await waitToLoad(() => this.fetchState === 'fetched')
      return ok(this.data)
    }

    this.fetchState = 'fetching'
    const remoteResult = await this.loadRemote2();

    if (!remoteResult.success) {
      const code = remoteResult.error.code
      switch (code) {
        case 'serialization-error':
        case 'sync-error':
          // we cannot do anything, overwrite
          reportNonFatalError(`'${remoteResult.error.code}' error while loading state, overwriting...`,
                              remoteResult.error.details)
          this.data = []
          this.fetchState = 'fetched'

          return ok(this.data)
        default:
          assertUnreachable(code)
          return remoteResult
      }
    }

    this.data = remoteResult.value;
    this.fetchState = 'fetched'

    return ok(this.data);

    function assertUnreachable (_: never): never {
      throw new Error('Didn\'t expect to get here')
    }
  }

  // private loadRemote() {
  //   const result = this.client.getValue('cmi.suspend_data');
  //   if (!result.success) {
  //     return result;
  //   }

  //   if (!result.value) {
  //     return ok(defaultSuspendDataState());
  //   }

  //   const deserialized = JSON.parse(result.value) as SuspendDataState;
  //   return ok(deserialized);
  // }

  private async loadRemote2(): Promise<Result<ScormSuspendDataState, Common.SuspendDataStoreError>> {
    debug('loadRemote')
    
    const result = await this.client.getValue('cmi.suspend_data');
    if (!result.success) {
      return err({ code: 'sync-error', details: result.error })
    }

    if (!result.value) {
      return ok([]);
    }

    let buffer
    try {
      buffer = base64.base64ToBytes(result.value)
    } catch (error) {
      debug('loadRemote base64 error', error)
      // try to load old json format
      const oldResult = this.loadRemoteOld(result.value)
      if (!oldResult.success) {
        debug('loadRemote old json format load error', oldResult.error)
        return err({ code: 'serialization-error', details: error })
      }
      return oldResult
    }

    let unzippedBuffer
    try {
      unzippedBuffer = pako.inflate(buffer);
    } catch (error) {
      debug('loadRemote unzip error', error)
      return err({ code: 'serialization-error', details: error })
    }
    
    const deserializeResult = this.serializer.deserialize(unzippedBuffer)
    if (!deserializeResult.success) {
      debug('loadRemote serialization error', deserializeResult.error)
      return err({ code: 'serialization-error', details: deserializeResult.error })
    }

    debug('loadRemote result', deserializeResult.value)
    return ok(deserializeResult.value)
  }

  private loadRemoteOld(serialized: string): Result<ScormSuspendDataState, Common.SuspendDataStoreError> {

    debug('trying to load old state', serialized)
    
    let result = []
    let deserialized
    try {
      deserialized = JSON.parse(serialized) as SuspendDataState;
    } catch (e) {
      return err({ code: 'serialization-error', details: { error: e, content: serialized } })
    }
    
    for (let key of Object.getOwnPropertyNames(deserialized)) {      
      result.push({ name: key, content: deserialized[key]})
    }

    if (result.find(x => x.name === 'exams2')) {
      debug('cleaning up old exam state', result)
      // delete old exams state
      result = result.filter(x => x.name !== 'exams')

      const exams2 = result.find(x => x.name === 'exams2')
      if (exams2) {
        exams2.name = 'exams'
      }
      debug('cleaned up and replaced exams2 to exams', result)
    }
      
    debug('loaded old state', result)
    
    return ok(result);
  }

  // private saveRemote(suspendData: SuspendDataState) {

  //   const serialized = JSON.stringify(suspendData);

  //   const result = this.client.setValue('cmi.suspend_data', serialized);
  //   if (!result.success) {
  //     return result;
  //   }

  //   return ok(true);
  // }

  private saveRemote2(state: ScormSuspendDataState): Result<true, Common.SuspendDataStoreError> {

    debug('saveRemote', state)
    
    const serializeResult = this.serializer.serialize(state)
    if (!serializeResult.success) {
      debug('saveRemote serialization error', serializeResult.error)
      return err({ code: 'serialization-error', details: serializeResult.error })
    }

    debug('saveRemote protobuf serialization length', serializeResult.value.length)

    const serializedBuffer = serializeResult.value
    
    const zipBuffer = pako.deflate(serializedBuffer);
    debug('saveRemote zip serialization length', zipBuffer.length)

    const base64Text = base64.bytesToBase64(zipBuffer)
    debug('saveRemote base64 serialization length', base64Text.length)

    console.log('STATE SERIALIZATION length', serializedBuffer.length,
                'ZIP BUFFER length', zipBuffer.length,
                'BASE64 length', base64Text.length)

    this.client.setValue('cmi.suspend_data', base64Text);
    // this.client.commit()

    return ok(true);
  }

}
